/*
 * Quick-add modal option dropdown (Figma XVnkapLdUTTodiEt75a4ag, node 572:3689).
 *
 * The modal's variant picker is cloned from the product page, so it arrives as
 * a native <select> marked .dc-variant-dropdown. That class is renamed to
 * .dc-qa-dropdown here: assets/dc-page-product.js builds its own panel on every
 * .dc-variant-dropdown in the document, and would otherwise also grab this one
 * when quick-add is opened from a product page.
 *
 * The <select> stays the real control — picking a row sets its value and fires
 * `change`, so variant-picker / product-form update the variant id exactly as
 * before. variant-picker re-renders (morphs) itself after every change, which
 * wipes the panel; a MutationObserver rebuilds it each time.
 */

const LABEL_TEXT = 'オプション';
const PRICE_SEPARATOR = ' - ';

let selfMutating = false;

/** Splits "name - price" (variant-main-picker.liquid's option text) at the last separator. */
function splitOptionText(text) {
  const clean = text.replace(/\s+/g, ' ').trim();
  const index = clean.lastIndexOf(PRICE_SEPARATOR);
  if (index === -1) return { name: clean, price: '' };
  return { name: clean.slice(0, index), price: clean.slice(index + PRICE_SEPARATOR.length) };
}

function buildOptionLine(text) {
  const { name, price } = splitOptionText(text);
  const line = document.createElement('span');
  line.className = 'dc-qa-dropdown__line';

  const nameEl = document.createElement('span');
  nameEl.textContent = name;
  line.appendChild(nameEl);

  if (price) {
    const sep = document.createElement('span');
    sep.className = 'dc-qa-dropdown__sep';
    sep.setAttribute('aria-hidden', 'true');
    const priceEl = document.createElement('span');
    priceEl.textContent = price;
    line.append(sep, priceEl);
  }
  return line;
}

function closePanel(option) {
  const panel = option.querySelector('.dc-qa-dropdown__panel');
  const trigger = option.querySelector('.variant-option__select-wrapper');
  if (panel) panel.hidden = true;
  trigger?.setAttribute('aria-expanded', 'false');
  option.classList.remove('dc-qa-dropdown--open');
}

function closeAll(content) {
  content.querySelectorAll('.dc-qa-dropdown--open').forEach(closePanel);
}

function enhanceOption(option) {
  option.classList.remove('dc-variant-dropdown');
  option.classList.add('dc-qa-dropdown');
  option.querySelectorAll('.dc-variant-dropdown__panel, .dc-qa-dropdown__panel').forEach((el) => el.remove());

  const trigger = option.querySelector('.variant-option__select-wrapper');
  const select = trigger?.querySelector('select.variant-option__select');
  if (!trigger || !select) return;

  trigger.setAttribute('role', 'button');
  trigger.setAttribute('tabindex', '0');
  trigger.setAttribute('aria-haspopup', 'listbox');
  trigger.setAttribute('aria-expanded', 'false');

  let label = trigger.querySelector('label');
  if (!label) {
    label = document.createElement('label');
    label.htmlFor = select.id;
    trigger.prepend(label);
  }
  label.className = 'dc-qa-dropdown__label';
  label.textContent = LABEL_TEXT;

  trigger.querySelector('.dc-qa-dropdown__value')?.remove();
  const value = document.createElement('span');
  value.className = 'dc-qa-dropdown__value';
  const selected = select.options[select.selectedIndex];
  if (selected) value.appendChild(buildOptionLine(selected.textContent));
  label.after(value);

  const panel = document.createElement('ul');
  panel.className = 'dc-qa-dropdown__panel';
  panel.setAttribute('role', 'listbox');
  panel.hidden = true;

  Array.from(select.options).forEach((opt, index) => {
    const row = document.createElement('li');
    row.className = 'dc-qa-dropdown__row';
    row.setAttribute('role', 'option');
    row.dataset.index = String(index);
    row.setAttribute('aria-selected', opt.selected ? 'true' : 'false');
    if (opt.selected) row.classList.add('is-selected');
    row.appendChild(buildOptionLine(opt.textContent));
    panel.appendChild(row);
  });

  option.appendChild(panel);
}

function enhance(content) {
  selfMutating = true;
  content.querySelectorAll('.variant-option--dropdowns').forEach(enhanceOption);
  requestAnimationFrame(() => {
    selfMutating = false;
  });
}

function bind(content) {
  content.addEventListener('click', (event) => {
    const target = /** @type {Element} */ (event.target);

    const row = target.closest('.dc-qa-dropdown__row');
    if (row) {
      const option = row.closest('.dc-qa-dropdown');
      const select = option?.querySelector('select.variant-option__select');
      const index = Number(row.dataset.index);
      if (option) closePanel(option);
      if (select && select.selectedIndex !== index) {
        select.selectedIndex = index;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    const trigger = target.closest('.dc-qa-dropdown .variant-option__select-wrapper');
    if (trigger) {
      // The <label for> would otherwise hand focus to the native <select>.
      event.preventDefault();
      const option = trigger.closest('.dc-qa-dropdown');
      const panel = option?.querySelector('.dc-qa-dropdown__panel');
      if (!option || !panel) return;

      const wasOpen = !panel.hidden;
      closeAll(content);
      if (!wasOpen) {
        panel.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        option.classList.add('dc-qa-dropdown--open');
      }
      return;
    }

    closeAll(content);
  });

  content.addEventListener('keydown', (event) => {
    const trigger = /** @type {Element} */ (event.target).closest?.('.dc-qa-dropdown .variant-option__select-wrapper');
    if (!trigger) return;

    if (event.key === 'Escape') {
      const option = trigger.closest('.dc-qa-dropdown');
      if (option?.classList.contains('dc-qa-dropdown--open')) {
        // Close just the list — without this the dialog's own Escape handler closes the whole modal.
        event.stopPropagation();
        closePanel(option);
      }
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      /** @type {HTMLElement} */ (trigger).click();
    }
  });

  let pending = 0;
  new MutationObserver(() => {
    if (selfMutating) return;
    cancelAnimationFrame(pending);
    pending = requestAnimationFrame(() => enhance(content));
  }).observe(content, { childList: true, subtree: true });
}

function init() {
  const content = document.getElementById('quick-add-modal-content');
  if (!content) return;
  bind(content);
  enhance(content);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
